import React from 'react';
import { Tooltip } from '@mui/material';
import {
  IconPhoneIncoming,
  IconPhoneOutgoing,
  IconPhone,
  IconPhonePause,
  IconPhoneOff,
  IconArrowsExchange,
} from '@tabler/icons-react';
import type { CallRecordListItem } from '@techgitter/contracts';

interface CallDirectionIconProps {
  call: Pick<CallRecordListItem, 'direction' | 'callKind'>;
  size?: number;
}

/**
 * Which way a call went, as one glyph with the meaning in its tooltip. Shared
 * by the Calls list and the job log on the technician card so the two can't
 * disagree about what an icon means.
 */
export const CallDirectionIcon: React.FC<CallDirectionIconProps> = ({ call, size = 16 }) => {
  // callKind is checked before direction: "nobody picked up" is the more useful
  // fact about a call than which way it was going. These kinds only appear on
  // rows the CDR pass has touched.
  if (call.callKind === 'missed' || call.callKind === 'unanswered') {
    const missed = call.callKind === 'missed';
    return (
      <Tooltip title={missed ? 'Missed — nobody here answered' : 'Unanswered — they did not pick up'}>
        <span
          aria-label={missed ? 'Missed call' : 'Unanswered call'}
          style={{ color: missed ? '#E74C3C' : '#E67E22', display: 'inline-flex' }}
        >
          <IconPhoneOff size={size} stroke={1.5} />
        </span>
      </Tooltip>
    );
  }
  if (call.direction === 'internal') {
    return (
      <Tooltip title="Internal — extension to extension">
        <span aria-label="Internal call" style={{ color: '#7F8C8D', display: 'inline-flex' }}>
          <IconArrowsExchange size={size} stroke={1.5} />
        </span>
      </Tooltip>
    );
  }
  if (call.callKind === 'voicemail') {
    return (
      <Tooltip title="Voicemail">
        <span aria-label="Voicemail" style={{ color: '#9B59B6', display: 'inline-flex' }}>
          <IconPhonePause size={size} stroke={1.5} />
        </span>
      </Tooltip>
    );
  }
  if (call.direction === 'inbound') {
    return (
      <Tooltip title="Inbound">
        <span aria-label="Inbound call" style={{ color: '#4A90D9', display: 'inline-flex' }}>
          <IconPhoneIncoming size={size} stroke={1.5} />
        </span>
      </Tooltip>
    );
  }
  if (call.direction === 'outbound') {
    return (
      <Tooltip title="Outbound">
        <span aria-label="Outbound call" style={{ color: '#1D9E75', display: 'inline-flex' }}>
          <IconPhoneOutgoing size={size} stroke={1.5} />
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Direction unknown">
      <span aria-label="Call, direction unknown" style={{ color: '#bbb', display: 'inline-flex' }}>
        <IconPhone size={size} stroke={1.5} />
      </span>
    </Tooltip>
  );
};
